import React, { useState, useEffect, useRef, useMemo } from "react";
import ModalCita from "./ModalCita";
import "./DayView.css";
import { getCitas, deleteCita } from "../../../../../services/citas";

const DayView = ({ selectedDate, refresh, setRefresh, horario }) => {
  // Generamos las horas en formato 24h
  const hours = Array.from({ length: 24 }, (_, i) => [
    `${i.toString().padStart(2, "0")}:00`,
    `${i.toString().padStart(2, "0")}:30`
  ]).flat();

  const [currentTime, setCurrentTime] = useState(new Date());
  const [citas, setCitas] = useState([]);
  const [citaSeleccionada, setCitaSeleccionada] = useState(null);
  const [mostrarModal, setMostrarModal] = useState(false);
  const hoursContainerRef = useRef(null);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  const isToday = selectedDate.toDateString() === new Date().toDateString();

  useEffect(() => {
    if (hoursContainerRef.current) {
      const scrollPosition = isToday
        ? (currentTime.getHours() * 2 + (currentTime.getMinutes() >= 30 ? 1 : 0)) * 30
        : 8 * 2 * 30;
      hoursContainerRef.current.scrollTop = scrollPosition;
    }
  }, [currentTime, selectedDate, isToday]);

  // Cargar citas desde el backend
  useEffect(() => {
    const fetchCitas = async () => {
      try {
        const data = await getCitas();

        // Convertimos selectedDate a YYYY-MM-DD respetando la zona horaria local
        const localDate = new Date(selectedDate);
        const dateString = `${localDate.getFullYear()}-${String(localDate.getMonth() + 1).padStart(2, "0")}-${String(localDate.getDate()).padStart(2, "0")}`;
        
        

        setCitas(data.filter((cita) => cita.fecha === dateString));
      } catch (error) {
        console.error("❌ Error al obtener citas:", error);
      }
    };

    fetchCitas();
  }, [selectedDate, refresh]);


  const processedCitas = useMemo(() => {
    // Preparamos las citas con tiempos en minutos y un índice auxiliar
    const citasCopy = citas.map((cita, idx) => {
      const [startHour, startMin] = cita.start_time.split(":").map(Number);
      const [endHour, endMin] = cita.end_time.split(":").map(Number);
      return {
        ...cita,
        startMinutes: startHour * 60 + startMin,
        endMinutes: endHour * 60 + endMin,
        _idx: idx, // índice auxiliar para DFS
      };
    });
  
    // Función que determina si dos citas se solapan
    const overlaps = (a, b) => {
      return a.startMinutes < b.endMinutes && b.startMinutes < a.endMinutes;
    };
  
    // Construimos clusters de solapamiento usando DFS
    const n = citasCopy.length;
    const visited = new Array(n).fill(false);
    const clusters = [];
  
    for (let i = 0; i < n; i++) {
      if (!visited[i]) {
        const cluster = [];
        const stack = [i];
        visited[i] = true;
        while (stack.length > 0) {
          const current = stack.pop();
          cluster.push(citasCopy[current]);
          for (let j = 0; j < n; j++) {
            if (!visited[j] && overlaps(citasCopy[current], citasCopy[j])) {
              visited[j] = true;
              stack.push(j);
            }
          }
        }
        clusters.push(cluster);
      }
    }
  
    // Para cada cluster, asignamos columnas con un algoritmo greedy
    clusters.forEach((cluster) => {
      // Ordenamos el cluster por hora de inicio
      cluster.sort((a, b) => a.startMinutes - b.startMinutes);
  
      const columns = []; // guardará el final del último evento en cada columna
      cluster.forEach((evento) => {
        let assigned = false;
        for (let i = 0; i < columns.length; i++) {
          if (evento.startMinutes >= columns[i]) {
            evento.column = i;
            columns[i] = evento.endMinutes;
            assigned = true;
            break;
          }
        }
        if (!assigned) {
          evento.column = columns.length;
          columns.push(evento.endMinutes);
        }
      });
      // Se calcula el ancho y left offset en función del número total de columnas
      const totalColumns = columns.length;
      cluster.forEach((evento) => {
        evento.widthPercentage = 100 / totalColumns;
        evento.leftOffset = evento.column * (100 / totalColumns);
      });
    });
  
    // Devolvemos una lista "aplanada" de todas las citas con la información de columnas
    return clusters.flat();
  }, [citas]);
  

  const handleGuardarCita = (nuevaCita) => {
    setCitas((prevCitas) => {
      const index = prevCitas.findIndex((c) => c.id === nuevaCita.id);
      if (index !== -1) {
        const nuevasCitas = [...prevCitas];
        nuevasCitas[index] = nuevaCita;
        return nuevasCitas;
      }
      return [...prevCitas, nuevaCita];
    });
    setMostrarModal(false);
    setRefresh((prev) => !prev);
  };

  const handleEliminarCita = async (id) => {
    try {
      const eliminado = await deleteCita(id);
      if (eliminado) {
        setCitas((prevCitas) => prevCitas.filter((c) => c.id !== id));
        setMostrarModal(false);
        setRefresh((prev) => !prev);
      } else {
        alert("Error al eliminar la cita en el servidor.");
      }
    } catch (error) {
      console.error("Error al eliminar la cita:", error);
      alert("Ocurrió un error al eliminar la cita.");
    }
  };

  const abrirModalNuevaCita = (hora) => {
    const correctDate = new Date(selectedDate);
    correctDate.setHours(12, 0, 0, 0); // 📌 Fijar la hora a mediodía antes de convertir a ISO
    const localDate = correctDate.toISOString().split("T")[0];
    setCitaSeleccionada({
      fecha: localDate,
      start_time: hora,
      end_time: "",
      motivo: "",
      paciente: ""
    });
    setMostrarModal(true);
  };

  const abrirModalEditarCita = (cita) => {
    setCitaSeleccionada(cita);
    setMostrarModal(true);
  };

  //esto es para pintar de gris los horarios que no sean del medico...
  const toMins = (hhmm) => {
  const [H, M] = (hhmm || "00:00").split(":").map(Number);
  return H * 60 + M;
};
const SLOT = 30; // minutos

// Día de la semana en español (mapea Date.getDay())
const WEEKDAYS = ["Domingo","Lunes","Martes","Miércoles","Jueves","Viernes","Sábado"];

// ¿El slot [slotStart, slotEnd) cae dentro de algún rango laboral?
const isWorkingSlot = (ranges, slotHHMM) => {
  if (!ranges || ranges.length === 0) return false;
  const start = toMins(slotHHMM);
  const end   = start + SLOT;
  return ranges.some(r => {
    const rs = toMins(r.start);
    const re = toMins(r.end);
    // Intersección estricta con el rango (sin permitir pegarse por 0 min)
    return start < re && rs < end;
  });
};

  return (
    <div className="day-view" ref={hoursContainerRef}>
      <div className="day-hours">
        {/* Render de las horas */}
        {hours.map((hour, index) => (
          <div
            key={index}
            
             className={`hour-block ${index % 2 !== 0 ? "half-hour-block" : ""} ${
              // gris si NO cae en horario laboral
              !isWorkingSlot(
                horario?.[WEEKDAYS[selectedDate.getDay()]] || [],
                hour
              )
                ? "off-hours"
                : ""
            }`}
            onClick={() => abrirModalNuevaCita(hour)}
          >
            <div className="hour-label">{hour.endsWith(":00") ? hour : ""}</div>
            <div className="hour-content">
              {isToday &&
                hour === `${currentTime.getHours().toString().padStart(2, "0")}:${currentTime.getMinutes() < 30 ? "00" : "30"}` && (
                  <div
                    className="current-time-line"
                    style={{ top: `${((currentTime.getMinutes() % 30) / 30) * 100}%` }}
                  ></div>
                )}
            </div>
          </div>
        ))}

        {/* Render de las citas */}
        {processedCitas.map((cita) => {
          // Calculamos la posición vertical y la altura en base a las horas
          const [startHour, startMin] = cita.start_time.split(":").map(Number);
          const [endHour, endMin] = cita.end_time.split(":").map(Number);
          const startIndex = startHour * 2 + (startMin >= 30 ? 1 : 0);
          const endIndex = endHour * 2 + (endMin >= 30 ? 1 : 0);
          const durationBlocks = endIndex - startIndex;

          return (
            <div
              key={cita.id + "-" + cita.startMinutes}
              className={`cita-block ${cita.status === "cancelada" ? "cancelada" : ""} ${cita.origen === "publico" ? "cita-en-linea" : ""}`}
              style={{
                top: `${startIndex * 30}px`,
                height: `${durationBlocks * 30}px`,
                width: `${cita.widthPercentage}%`,
                left: `${cita.leftOffset}%`,
                position: "absolute"
              }}
              onClick={(e) => {
                e.stopPropagation();
                abrirModalEditarCita(cita);
              }}
            >
             {cita.origen === "publico" && "🌐 "}{cita.paciente_nombre} - {cita.motivo}
            </div>
          );
        })}
      </div>

      {mostrarModal && (
        <ModalCita
          cita={citaSeleccionada}
          onGuardar={handleGuardarCita}
          onEliminar={handleEliminarCita}
          onCerrar={() => setMostrarModal(false)}
        />
      )}
    </div>
  );
};

export default DayView;
